/* ============================================================================
   Requests module — shared data layer.
   Types, the lookup fetch/cache hook, and small presentation helpers used by
   the Dashboard, the intake form, the detail page, and the Overview tab.
   All network access goes through lib/api.ts.
   ============================================================================ */

import { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';

/* --- Lookup shapes -------------------------------------------------------- */
export interface LookupOption {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  color?: string | null;
}

export interface Lookups {
  priorities: LookupOption[];
  statuses: LookupOption[];
  collateralTypes: LookupOption[];
  channels: LookupOption[];
  objectives: LookupOption[];
  metrics: LookupOption[];
  designStyles: LookupOption[];
  formats: LookupOption[];
}

const EMPTY_LOOKUPS: Lookups = {
  priorities: [],
  statuses: [],
  collateralTypes: [],
  channels: [],
  objectives: [],
  metrics: [],
  designStyles: [],
  formats: [],
};

// Module-level cache so the lookups are fetched once per session.
let cache: Lookups | null = null;
let inflight: Promise<Lookups> | null = null;

async function fetchLookups(): Promise<Lookups> {
  if (cache) return cache;
  if (!inflight) {
    inflight = apiGet<Lookups>('/api/lookups')
      .then((data) => {
        cache = { ...EMPTY_LOOKUPS, ...data };
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Fetch the app's lookup tables once and cache them for the session. */
export function useLookups(): { lookups: Lookups; loading: boolean } {
  const [lookups, setLookups] = useState<Lookups>(cache ?? EMPTY_LOOKUPS);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let active = true;
    if (cache) {
      setLookups(cache);
      setLoading(false);
      return;
    }
    fetchLookups()
      .then((l) => {
        if (active) {
          setLookups(l);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { lookups, loading };
}

/* --- Row / detail shapes -------------------------------------------------- */
export interface RequestRow {
  id: number;
  ticketId: string;
  title: string;
  requesterName: string;
  designerName: string | null;
  priority: string | null;
  priorityCode: string | null;
  designStatus: string;
  statusCode: string;
  statusColor: string | null;
  requestDate: string | null;
  neededBy: string | null;
  actualCompletion: string | null;
  isDraft: number;
}

export interface AttachmentRow {
  id: number;
  filename: string;
  blobPath: string;
  downloadUrl: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  kind: string | null;
  uploadedBy: number | null;
  uploadedAt: string | null;
}

export interface RequestDetail {
  id: number;
  ticketId: string;
  title: string;
  description: string | null;
  orgId: number | null;
  orgName: string | null;
  requesterId: number;
  requesterName: string;
  requesterEmail: string;
  designerId: number | null;
  designerName: string | null;
  priorityCode: string | null;
  priorityName: string | null;
  statusCode: string;
  statusName: string;
  statusColor: string | null;
  statusIsTerminal: number;
  collateralTypeCode: string | null;
  collateralTypeName: string | null;
  designStyleCode: string | null;
  designStyleName: string | null;
  requestDate: string | null;
  neededBy: string | null;
  actualCompletion: string | null;
  isDraft: number;
  cta: string | null;
  targetAudience: string | null;
  audienceAge: string | null;
  audienceGender: string | null;
  audienceOther: string | null;
  sizes: string | null;
  contentCopy: string | null;
  campaignTheme: string | null;
  preferredColors: string | null;
  preferredImagery: string | null;
  notes: string | null;
  objectives: string[];
  metrics: string[];
  channels: string[];
  formats: string[];
  attachments: AttachmentRow[];
}

/* --- Presentation helpers ------------------------------------------------- */

/** Map a design-status code to its Direction-C pill variant class. */
export function statusPillClass(code: string): string {
  switch (code) {
    case 'in_progress':
      return 'pill-in-progress';
    case 'waiting_content':
      return 'pill-waiting-content';
    case 'waiting_approval':
      return 'pill-waiting-approval';
    case 'revision_requested':
      return 'pill-revision';
    case 'complete':
      return 'pill-complete';
    case 'canceled':
      return 'pill-canceled';
    case 'new':
    default:
      return 'pill-new';
  }
}

/** Map a priority code to a subtle semantic pill (urgent/high stand out). */
export function priorityPillClass(code: string | null): string {
  switch (code) {
    case 'urgent':
      return 'pill-danger';
    case 'high':
      return 'pill-warning';
    case 'low':
      return 'pill-info';
    default:
      return '';
  }
}

/** Human label for a lookup code, falling back to a de-slugged code. */
export function labelFor(options: LookupOption[], code: string): string {
  const hit = options.find((o) => o.code === code);
  if (hit) return hit.name;
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
